/**
 * Diplomacy (§2): in-game alliances (FFA pairs only: requests, acceptance, betrayal locks),
 * ceasefires (timed mutual non-aggression, FFA, any number per player), gifts of credits and
 * marks (a target pointed out to our ally / teammates).
 */
import { BALANCE } from '../balance.js';
import { player, spend } from '../entities.js';
import type { MatchState, PlayerId, SimEvent } from '../types.js';

const A = BALANCE.alliance;
const D = BALANCE.diplomacy;

/** the pair (x, y) in either order */
const pairOf = (x: PlayerId, y: PlayerId) => (r: { from: PlayerId; to: PlayerId }) => (r.from === x && r.to === y) || (r.from === y && r.to === x);

export function requestAlliance(state: MatchState, from: PlayerId, to: PlayerId, events: SimEvent[]): void {
  const expiresTick = state.tick + A.requestTicks;
  state.alliances.requests.push({ from, to, expiresTick });
  events.push({ type: 'allyPrompt', from, to, expiresTick });
}

export function respondAlliance(state: MatchState, me: PlayerId, from: PlayerId, accept: boolean, events: SimEvent[]): void {
  const a = state.alliances;
  const asked = a.requests.some((r) => r.from === from && r.to === me);
  a.requests = a.requests.filter((r) => !(r.from === from && r.to === me));
  if (!accept) {
    // the proposer hears it at once instead of watching the offer run out
    if (asked) events.push({ type: 'phase', phase: { kind: 'allyDeclined', a: from, b: me, tick: state.tick } });
    return;
  }
  formAlliance(state, from, me, events);
}

export function formAlliance(state: MatchState, x: PlayerId, y: PlayerId, events: SimEvent[]): void {
  const a = state.alliances;
  a.allyOf[x] = y;
  a.allyOf[y] = x;
  // pairs only: drop every other pending request involving either player
  a.requests = a.requests.filter((r) => r.from !== x && r.to !== x && r.from !== y && r.to !== y);
  // the alliance covers what a ceasefire between them did
  a.ceasefires = a.ceasefires.filter((c) => !((c.a === x && c.b === y) || (c.a === y && c.b === x)));
  a.ceasefireRequests = a.ceasefireRequests.filter((r) => !pairOf(x, y)(r));
  events.push({ type: 'phase', phase: { kind: 'ally', a: Math.min(x, y), b: Math.max(x, y), tick: state.tick } });
}

export function breakAlliance(state: MatchState, breaker: PlayerId, ex: PlayerId, events: SimEvent[]): void {
  const a = state.alliances;
  a.allyOf[breaker] = 0;
  a.allyOf[ex] = 0;
  a.betrayals.push({ breaker, target: ex, untilTick: state.tick + A.breakNoTargetTicks });
  a.noAllyUntil[breaker] = state.tick + A.breakNoAllyTicks;
  events.push({ type: 'phase', phase: { kind: 'allyBroken', a: breaker, b: ex, tick: state.tick } });
}

/**
 * Dissolve any alliance of an eliminated player (no betrayal penalties). The dissolved pair is
 * announced like a break (`allyBroken`, a = the eliminated player) so views drop the alliance.
 */
export function clearAlliances(state: MatchState, p: PlayerId, events: SimEvent[]): void {
  const a = state.alliances;
  const ally = a.allyOf[p];
  if (ally) {
    a.allyOf[ally] = 0;
    events.push({ type: 'phase', phase: { kind: 'allyBroken', a: p, b: ally, tick: state.tick } });
  }
  a.allyOf[p] = 0;
  a.requests = a.requests.filter((r) => r.from !== p && r.to !== p);
  a.ceasefires = a.ceasefires.filter((c) => c.a !== p && c.b !== p);
  a.ceasefireRequests = a.ceasefireRequests.filter((r) => r.from !== p && r.to !== p);
}

export function requestCeasefire(state: MatchState, from: PlayerId, to: PlayerId, events: SimEvent[]): void {
  state.alliances.ceasefireRequests.push({ from, to, expiresTick: state.tick + D.ceasefireRequestTicks });
  events.push({ type: 'phase', phase: { kind: 'ceasefireOffer', a: from, b: to, tick: state.tick } });
}

export function respondCeasefire(state: MatchState, me: PlayerId, from: PlayerId, accept: boolean, events: SimEvent[]): void {
  const a = state.alliances;
  const asked = a.ceasefireRequests.some((r) => r.from === from && r.to === me);
  if (!accept) {
    a.ceasefireRequests = a.ceasefireRequests.filter((r) => !(r.from === from && r.to === me));
    if (asked) events.push({ type: 'phase', phase: { kind: 'ceasefireDeclined', a: from, b: me, tick: state.tick } });
    return;
  }
  // both offers between the two are answered by this one
  a.ceasefireRequests = a.ceasefireRequests.filter((r) => !pairOf(from, me)(r));
  const lo = Math.min(from, me);
  const hi = Math.max(from, me);
  a.ceasefires.push({ a: lo, b: hi, untilTick: state.tick + D.ceasefireTicks });
  events.push({ type: 'phase', phase: { kind: 'ceasefire', a: lo, b: hi, tick: state.tick } });
}

/** Breaking early is public; the breaker may not strike the other for a while and is locked out of new ceasefires. */
export function breakCeasefire(state: MatchState, breaker: PlayerId, other: PlayerId, events: SimEvent[]): void {
  const a = state.alliances;
  a.ceasefires = a.ceasefires.filter((c) => !((c.a === breaker && c.b === other) || (c.a === other && c.b === breaker)));
  a.betrayals.push({ breaker, target: other, untilTick: state.tick + D.ceasefireBreakNoTargetTicks });
  a.noCeasefireUntil[breaker] = state.tick + D.ceasefireBreakCooldownTicks;
  events.push({ type: 'phase', phase: { kind: 'ceasefireBroken', a: breaker, b: other, tick: state.tick } });
}

export function giftCredits(state: MatchState, from: PlayerId, to: PlayerId, amount: number, events: SimEvent[]): void {
  const p = player(state, from)!;
  spend(state, p, 'gift', amount);
  player(state, to)!.credits += amount;
  events.push({ type: 'gift', from, to, amount, tick: state.tick });
}

export function markTarget(state: MatchState, from: PlayerId, target: PlayerId, events: SimEvent[]): void {
  state.alliances.markReadyTick[from] = state.tick + D.markCooldownTicks;
  events.push({ type: 'phase', phase: { kind: 'mark', a: from, b: target, tick: state.tick } });
}

/** Expire requests, ceasefires and betrayal locks. */
export function alliancesSystem(state: MatchState): void {
  const a = state.alliances;
  const t = state.tick;
  if (a.requests.length) a.requests = a.requests.filter((r) => r.expiresTick > t);
  if (a.betrayals.length) a.betrayals = a.betrayals.filter((b) => b.untilTick > t);
  if (a.ceasefireRequests.length) a.ceasefireRequests = a.ceasefireRequests.filter((r) => r.expiresTick > t);
  if (a.ceasefires.length) a.ceasefires = a.ceasefires.filter((c) => c.untilTick > t);
}
